import axios from "axios";

/**
 * Formats an axios error into a human-readable toast message.
 *
 * @param {import("axios").AxiosError} error - The axios error object
 * @returns {string} Formatted error message
 */
export function formatAxiosError(error) {
  if (!error.response) {
    return "Network Error: Unable to reach server";
  }

  const { status, statusText } = error.response;
  const method = (error.config?.method || "UNKNOWN").toUpperCase();
  const url = error.config?.url || "unknown URL";

  // Try to extract a message from the response body
  const responseMessage =
    error.response.data?.message ||
    error.response.data?.error ||
    (typeof error.response.data === "string" ? error.response.data : null);

  const baseMessage = `API ${method} ${url} — ${status} ${statusText}`;

  return responseMessage ? `${baseMessage}: ${responseMessage}` : baseMessage;
}

/**
 * Sets up an axios response interceptor that reports failed HTTP requests.
 *
 * @param {Object} config
 * @param {Function} config.onError - Callback invoked with (formattedMessage, errorKey)
 * @returns {number} Interceptor ID (used for teardown)
 */
export function setupAxiosInterceptor({ onError }) {
  const interceptorId = axios.interceptors.response.use(null, (error) => {
    const message = formatAxiosError(error);

    console.error("[AxiosInterceptor] Request failed:", message, error);

    if (typeof onError === "function") {
      onError(message);
    }

    return Promise.reject(error);
  });

  return interceptorId;
}

/**
 * Removes a previously registered axios interceptor.
 *
 * @param {number} interceptorId - The ID returned by setupAxiosInterceptor
 */
export function teardownAxiosInterceptor(interceptorId) {
  axios.interceptors.response.eject(interceptorId);
}
